import get from 'lodash/get';
import { useCallback, useMemo } from 'react';
import { Doc as YDoc, type Map as YMap } from 'yjs';

import { WidgetType } from 'shared/common/utils/widgetsUtils';
import { getRootMap } from 'shared/common/utils/yjs';
import { getValidatedContentObjectsFromDocument } from 'shared/common/widgets/utils/getValidatedContentObjects';
import { useObservedArray, useObservedProperty } from 'shared/utils/yjs.client';

import { useActiveDocument } from '../../components/providers/ActiveDocumentProvider/ActiveDocumentProvider';

export type Page = {
  isHidden: boolean;
  title: string;
  id: string;
  currentIndex: number;
};

const getCurrentIndex = (rootChildren: string[], pageId: string) => {
  return rootChildren.findIndex(element => element === pageId);
};

const sortPages = (pageA: Page, pageB: Page) => pageA.currentIndex - pageB.currentIndex;

const getPagesInOrder = (array: Page[]) => array.filter(({ currentIndex }) => currentIndex >= 0).sort(sortPages);

// Stable stub used to satisfy unconditional hook calls in the dynamic path when in static mode.

export function usePages() {
  const { document, isStatic } = useActiveDocument();
  return isStatic ? useStaticPages(document) : useDynamicPages(document);
}

function useDynamicPages(document: YDoc) {
  const [rootChildren] = useObservedArray<string>(getRootMap(document), 'children');

  const selector = useCallback(
    (contentObjects: YMap<YMap<unknown>>) => {
      const pages: Record<'pagesData' | 'visiblePages' | 'hiddenPages', Page[]> = {
        pagesData: [],
        visiblePages: [],
        hiddenPages: []
      };

      contentObjects.forEach(contentObject => {
        const type = contentObject.get('type') as WidgetType;
        if (type === WidgetType.Page || type === WidgetType.Slide) {
          const id = contentObject.get('id') as string;
          const title = contentObject.get('title') as string;
          const isHidden = !!contentObject.get('isHidden');
          const currentIndex = getCurrentIndex(rootChildren, id);

          const fullPage = { id, title, isHidden, currentIndex };

          pages.pagesData.push(fullPage);
          if (isHidden) {
            pages.hiddenPages.push(fullPage);
          } else {
            pages.visiblePages.push(fullPage);
          }
        }
      });

      return {
        pagesData: getPagesInOrder(pages.pagesData),
        visiblePages: getPagesInOrder(pages.visiblePages),
        hiddenPages: getPagesInOrder(pages.hiddenPages)
      };
    },
    [rootChildren]
  );

  const [pages] = useObservedProperty<Record<'pagesData' | 'visiblePages' | 'hiddenPages', Page[]>>(
    getRootMap(document),
    'contentObjects',
    {
      deepObserve: true,
      selector
    }
  );

  return pages;
}

function useStaticPages(document: Record<string, unknown>) {
  return useMemo(() => {
    const rootChildren = get(document, 'staticContent.children') as unknown as string[]; // array
    const validatedContentObjects = getValidatedContentObjectsFromDocument(document);

    const pages: Record<'pagesData' | 'visiblePages' | 'hiddenPages', Page[]> = {
      pagesData: [],
      visiblePages: [],
      hiddenPages: []
    };

    Object.values(validatedContentObjects).forEach(contentObject => {
      if (contentObject.type === WidgetType.Page || contentObject.type === WidgetType.Slide) {
        const { id = '', title = '', isHidden = false } = contentObject;

        const currentIndex = getCurrentIndex(rootChildren, id);

        const fullPage = { id, title, isHidden, currentIndex };

        pages.pagesData.push(fullPage);
        if (isHidden) {
          pages.hiddenPages.push(fullPage);
        } else {
          pages.visiblePages.push(fullPage);
        }
      }
    });

    return {
      pagesData: getPagesInOrder(pages.pagesData),
      visiblePages: getPagesInOrder(pages.visiblePages),
      hiddenPages: getPagesInOrder(pages.hiddenPages)
    };
  }, [document]);
}
